<script setup lang="ts">
import type { MaybeElementRef } from "@vueuse/core";
import type { ShortcutJs } from "@/generated/models";
import { useFocus } from "@vueuse/core";
import TextField from "ls/components/form/TextField.vue";
import { ref, useTemplateRef } from "vue";
import LinkSuggestions from "./LinkSuggestions.vue";

defineProps<{
  shortcut: string;
  filterFiles?: (file: ShortcutJs) => boolean;
}>();

const emit = defineEmits<{
  submit: [string];
  cancel: [];
}>();

const model = defineModel<string>({ default: "" });

const suggestionsRef = useTemplateRef("suggestions");
const suggestedItem = ref<string | undefined>();

function submitValue(value: string) {
  model.value = value;
  emit("submit", value);
}

function handleKeyDownEnter() {
  if (suggestedItem.value) {
    submitValue(suggestedItem.value);
    return;
  }
  if (!model.value) return;
  submitValue(model.value);
}

const urlField = useTemplateRef("urlField");
const urlFocus = useFocus(urlField as MaybeElementRef, { initialValue: true });
function focus() {
  urlFocus.focused.value = true;
}
defineExpose({
  focus,
});
</script>

<template>
  <div
    class="flex min-h-0 flex-col gap-3"
    @keydown.esc.prevent="emit('cancel')"
  >
    <div class="flex flex-col">
      <TextField
        ref="urlField"
        v-model="model"
        placeholder="Paste a URL or search by file name or ID"
        @keydown.down.prevent="suggestionsRef?.highlightNext()"
        @keydown.up.prevent="suggestionsRef?.highlightPrevious()"
        @keydown.enter.prevent="handleKeyDownEnter"
      />
      <slot name="error" />
    </div>
    <LinkSuggestions
      ref="suggestions"
      v-model="suggestedItem"
      :search-term="model"
      :filter-files="filterFiles"
      :shortcut="shortcut"
      @select="submitValue"
    />
  </div>
</template>
